// Miami MVP — reusable industry configuration.
// Add a new industry by appending a record; the page, flow diagram,
// calculator prefill and lead form all read from here.

export const PEOPLE_BUCKETS = ["1–5", "6–10", "11–25", "25+"];

export const CAPACITY_QUESTIONS = [
  { id: "searching", label: "Searching for information" },
  { id: "reentering", label: "Entering information twice" },
  { id: "chasing", label: "Chasing updates" },
  { id: "errors", label: "Fixing errors" },
  { id: "moving", label: "Moving information between departments" },
];

export const CAPACITY_EXAMPLE = [
  { role: "Sales", hours: 8 },
  { role: "Operations", hours: 12 },
  { role: "Accounting", hours: 6 },
  { role: "Management", hours: 4 },
];

export const INDUSTRIES = [
  {
    id: "construction",
    label: "Construction",
    process: "Project-to-Payment",
    roles: ["Estimator", "Project Manager", "Site", "Accounting", "Owner"],
    headline: "Project-to-payment, without the chase.",
    message: "Everyone works from the same project record, but each person gets exactly what they need.",
    info: ["customer", "contract", "drawings", "RFIs", "submittals", "schedule", "change orders", "labour", "materials", "progress", "invoices"],
    pains: [
      "RFIs wait in inboxes",
      "Change orders get priced twice",
      "Invoices wait for site confirmations",
      "Status lives in phone calls",
    ],
    cta: "Find My Bottleneck",
  },
  {
    id: "hvac",
    label: "HVAC",
    process: "Lead-to-Service",
    roles: ["Sales", "Dispatcher", "Technician", "Customer", "Accounting"],
    headline: "Lead to service call, with nothing re-typed.",
    message: "Everyone works from the same customer/job record without giving every role information they don't need.",
    info: ["lead", "customer", "equipment", "quote", "appointment", "work order", "technician", "parts", "invoice", "payment"],
    pains: [
      "Technicians call the office for job details",
      "Quotes stall after the site visit",
      "Parts get re-ordered by phone",
      "Invoices wait for paper work orders",
    ],
    cta: "Find My Bottleneck",
  },
  {
    id: "property-management",
    label: "Property Management",
    process: "Tenant-to-Resolution",
    roles: ["Tenant", "Property Manager", "Maintenance", "Vendor", "Accounting"],
    headline: "Tenant request to resolution, on one record.",
    message: "One source of truth. Different workspaces for different roles.",
    info: ["tenant", "property", "unit", "request", "maintenance", "vendor", "work order", "cost", "invoice", "resolution"],
    pains: [
      "Requests arrive by text, email, and phone",
      "Tenants chase for updates",
      "Vendor costs get reconciled at month-end",
      "Nobody knows what's still open",
    ],
    cta: "Find My Bottleneck",
  },
  {
    id: "plumbing-field-service",
    label: "Plumbing / Field Service",
    process: "Call-to-Cash",
    roles: ["Lead", "Dispatch", "Technician", "Job", "Invoice", "Payment"],
    headline: "Call to cash, without the whiteboard.",
    message: "One record from the first call to the final payment — nothing re-entered along the way.",
    info: ["lead", "scheduling", "dispatch", "technician information", "job notes", "parts", "photos", "customer communication", "invoicing", "payment follow-up"],
    pains: [
      "The schedule is a whiteboard",
      "Techs text job photos to the office",
      "Invoices go out days after the job",
      "Payment follow-up depends on memory",
    ],
    cta: "Find My Bottleneck",
  },
  {
    id: "other",
    label: "Other Service Business",
    process: "Intake-to-Invoice",
    roles: ["Intake", "Delivery", "Review", "Accounting", "Owner"],
    headline: "Your process, mapped once — then flowing.",
    message: "The same idea, fitted to your business: one record, clear roles, no chasing between them.",
    info: ["request", "scope", "schedule", "work", "review", "invoice", "payment"],
    pains: [
      "Everything routes through one person",
      "Status lives in inboxes",
      "Invoicing waits for month-end",
      "The same data gets entered twice",
    ],
    cta: "Find My Bottleneck",
  },
];

export const getIndustry = (id) => INDUSTRIES.find((i) => i.id === id) || null;

// All MiamiPage UI strings (EN) — the page is a pure renderer of this copy
// object so the same flow serves /es/miami with miamiConfigEs.js.
export const MIAMI_EN = {
  docTitle: "OceanData Miami — Give your team back the time they spend managing the work",
  eyebrow: "OceanData · Miami-Dade",
  heroLine1: "Give your team back the time",
  heroLine2: "they spend managing the work.",
  heroSub:
    "OceanData connects your people, information and processes so work moves from one role to the next without unnecessary friction, duplication and chasing.",
  ctaBottleneck: "Find My Bottleneck",
  ctaCalculate: "Calculate My Capacity",
  stepOneEyebrow: "Step one",
  selectorTitle: "One process. One industry. One source of truth.",
  selectorSub: "What type of business are you?",
  oneRecordCarries: "One record carries",
  flowCtaHint: "Then see what the hours are worth, below.",
  flowEmpty: "Choose your industry above — we'll show you the exact process we can simplify first.",
  howEyebrow: "How OceanData works",
  howTitle: "Five steps. One flow.",
  steps: [
    "Choose the process",
    "Map the roles",
    "Centralize the information",
    "Automate the handoffs",
    "Measure capacity recovered",
  ],
  roiEyebrow: "The ROI is capacity",
  roiTitle: "What one simplified process returned to one team.",
  perWeek: "per week",
  roiTotal: "= 30 hours a week, recovered.",
  roiCta: "Calculate Your Capacity",
  calcEyebrow: "Capacity calculator",
  calcTitle: "Two minutes. Honest arithmetic.",
  calcSub: "Estimate loosely — ranges are fine. This is a conversation starter, not an audit.",
  peopleQ: "How many people touch this process?",
  hoursQ: "How many hours per week are lost to:",
  hoursHint: "Across everyone involved, per week.",
  rateLabel: "Optional: loaded hourly cost",
  calcRun: "Calculate My Capacity",
  resultEyebrow: "Estimated Capacity Opportunity",
  perYear: "per year (× 52 weeks)",
  valueSuffix: "of capacity per year",
  disclaimer:
    "Illustrative estimate: hours lost per week × 52 weeks × your loaded hourly cost. An estimate with explicit assumptions, not a measured result — the free review replaces it with numbers from your own week.",
  formEyebrow: "Free capacity review",
  formTitle: "Let's find where your capacity is going.",
  formSub:
    "Fifteen minutes with a person who knows where hours hide. No pitch, no sequence — a reply within one business day.",
  successTitle: "On our desk.",
  successBody:
    "A person — usually Bruno — will reply within one business day with two or three questions about where your team's time goes.",
  industryPh: "Industry",
  bottleneckPh: "Biggest process bottleneck",
  fallbackPain: "Wherever hours disappear",
  peoplePh: "People involved",
  weeklyHoursPh: "Weekly hours lost (est.)",
  namePh: "Name",
  companyPh: "Company",
  emailPh: "Work email",
  phonePh: "Phone (optional)",
  submitIdle: "Show Me Where Capacity Is Being Lost",
  submitSending: "Sending…",
  privacy: "We use this only to reply to you. Nothing else.",
  finalTitle: "How much capacity could we give back to your team?",
  finalSub:
    "We don't sell software. We give your team capacity back by fixing how work moves through the business.",
  finalCta: "Find Out",
  toastCalcError: "Choose a team size and enter at least one weekly-hour estimate.",
  toastSuccess: "Received — a person will reply within one business day.",
  toastError: "Something didn't go through. Please try again.",
  relatedLabel: "Software guides",
  relatedLinks: [
    { label: "Zoho FSM for field service", href: "/zoho-fsm" },
    { label: "Zoho FSM accounting integrations", href: "/zoho-fsm-accounting" },
    { label: "Zoho Projects for construction", href: "/zoho-projects-construction" },
    { label: "Connected buildings with Zoho IoT", href: "/zoho-iot-connected-buildings" },
    { label: "Zoho FSM vs Zuper", href: "/zoho-fsm-vs-zuper" },
  ],
};
